import React from 'react'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import {
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
  ReportFavoriteElement,
  ReportSection,
  useGetReportElementsQuery,
  useGetReportFavoriteElementsQuery,
} from 'gqlTypes'
import { Navigate } from 'react-router'
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material'
import ReportElement from './ReportElement/ReportElement'
import ReportElementFavorite from './ReportElement/ReportElementFavorite'
import ReportFavoriteLabel from './ReportFavoriteLabel'
import { Link } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import StarIcon from '@mui/icons-material/Star'

const ReportElementsSkeleton = () => (
  <Paper sx={{ width: '100%', p: 4 }}>
    <Stack spacing={1}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="text" />
      ))}
      <Skeleton variant="rounded" height={200} />
    </Stack>
  </Paper>
)

interface Props {
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  sectionId: string
  onUpdateFavorites: () => void
}
const ReportFavoriteElements: React.FC<Props> = ({
  report,
  sectionId,
  onUpdateFavorites,
}) => {
  const reportId = report.id
  const { data, loading, refetch } = useGetReportFavoriteElementsQuery({
    variables: {
      reportId: reportId,
      sectionId: sectionId,
    },
  })
  const {
    data: allData,
    refetch: allRefetch,
    loading: allLoading,
  } = useGetReportElementsQuery({
    variables: { reportId, sectionId },
  })
  if (loading) {
    return <ReportElementsSkeleton />
  }
  const currentSection = report.sections.find(
    (section) => section.id === sectionId
  )
  const parentSection = currentSection?.parentReportSectionId
    ? report.sections.find(
        (section) => section.id === currentSection.parentReportSectionId
      )
    : undefined

  const handleUpdateFavorites = () => {
    refetch()
    allRefetch()
    onUpdateFavorites()
  }

  // if there are no favorite sections there are no elements to show
  if (report.sections.length === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert
          severity="info"
          variant="filled"
          iconMapping={{ info: <StarIcon color="primary" /> }}
          action={
            <Button
              size="small"
              endIcon={<ArrowForwardIcon color="primary" />}
              component={Link}
              to={`/reports/${report.id}/sections/${sectionId}`}
              sx={{ alignSelf: 'center' }}
            >
              Return to the full report
            </Button>
          }
        >
          <AlertTitle>Bookmarks</AlertTitle>
          You haven't bookmarked anything for this report yet. Return to the
          full report to add bookmarks.
        </Alert>
      </Box>
    )
  }

  // if the section id isn't found in the favorite sections try to find one
  if (!currentSection) {
    const firstParentSection = report.sections.filter(
      (d) => !d.parentReportSectionId
    )[0]
    const firstSection = report.sections[0]
    if (firstParentSection) {
      return (
        <Navigate
          replace
          to={`/report_bookmarks/${report.id}/sections/${firstParentSection.id}`}
        />
      )
    }
    if (firstSection) {
      return (
        <Navigate
          replace
          to={`/report_bookmarks/${report.id}/sections/${firstSection.id}`}
        />
      )
    }
  }

  // section is in favorites but doesn't have elements to show, send to the first child
  if (currentSection && data?.reportFavoriteElements.length === 0) {
    const firstChildSection = report.sections.filter(
      (d: ReportSection) => d.parentReportSectionId === currentSection.id
    )[0]
    const firstSection = report.sections[0]
    if (firstChildSection) {
      return (
        <Navigate
          replace
          to={`/report_bookmarks/${report.id}/sections/${firstChildSection.id}`}
        />
      )
    }
    if (firstSection) {
      return (
        <Navigate
          replace
          to={`/report_bookmarks/${report.id}/sections/${firstSection.id}`}
        />
      )
    }
  }

  return (
    <Paper sx={{ width: '100%' }}>
      <Grid
        container
        p={4}
        pt={2}
        direction={{
          xs: 'column-reverse',
          sm: 'column-reverse',
          md: 'column-reverse',
          lg: 'row',
          xl: 'row',
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={9}
          display="flex"
          alignContent="center"
          alignItems="center"
        >
          <Box>
            <ReportFavoriteLabel color="primary" />
            <Typography
              component="small"
              sx={{ color: '#72828E', fontSize: '0.9rem' }}
            >
              {report.title}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={3}>
          {currentSection && (
            <Box display="flex" justifyContent={'flex-end'}>
              <ReportElementFavorite
                id={currentSection.id}
                type={ReportFavoriteElement.Section}
                report={report}
                sectionId={sectionId}
                onUpdate={handleUpdateFavorites}
                favorites
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Box px={4} mb={4}>
        {parentSection && (
          <Typography variant="h5" color="secondary">
            {parentSection.title}
          </Typography>
        )}
        {currentSection && (
          <Typography variant="h2">{currentSection.title}</Typography>
        )}
      </Box>
      {currentSection &&
        data?.reportFavoriteElements.map((element) => {
          return (
            <Box key={element.id} sx={{ display: 'flex', width: '100%' }}>
              <ReportElement
                favorites
                reportId={report.id}
                report={report}
                element={element}
                sectionId={sectionId}
                onUpdateFavorites={handleUpdateFavorites}
              />
            </Box>
          )
        })}
    </Paper>
  )
}

export default ReportFavoriteElements
